import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, Euro, Receipt, PiggyBank, 
  Calendar, Plus, Trash2, Edit, Settings, ArrowRight,
  Building, Home, Zap, Droplets, ShoppingCart, Wrench
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EXPENSE_CATEGORIES = [
  { value: 'fitto', label: 'Fitto/Affitto', icon: Home, color: 'bg-blue-100 text-blue-600' },
  { value: 'lavanderia', label: 'Lavanderia', icon: Droplets, color: 'bg-cyan-100 text-cyan-600' },
  { value: 'biancheria', label: 'Biancheria', icon: ShoppingCart, color: 'bg-purple-100 text-purple-600' },
  { value: 'pulizie', label: 'Pulizie', icon: Home, color: 'bg-teal-100 text-teal-600' },
  { value: 'luce', label: 'Luce', icon: Zap, color: 'bg-yellow-100 text-yellow-600' },
  { value: 'gas', label: 'Gas', icon: Zap, color: 'bg-orange-100 text-orange-600' },
  { value: 'utenze', label: 'Utenze', icon: Building, color: 'bg-indigo-100 text-indigo-600' },
  { value: 'manutenzione', label: 'Manutenzione', icon: Wrench, color: 'bg-red-100 text-red-600' },
  { value: 'forniture', label: 'Forniture', icon: ShoppingCart, color: 'bg-pink-100 text-pink-600' },
  { value: 'tasse', label: 'Tasse', icon: Building, color: 'bg-gray-100 text-gray-600' },
  { value: 'varie', label: 'Varie', icon: Receipt, color: 'bg-green-100 text-green-600' }
];

const PAYMENT_METHODS = [
  { value: 'contanti', label: 'Contanti' },
  { value: 'carta', label: 'Carta di credito/debito' },
  { value: 'bonifico', label: 'Bonifico bancario' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'altro', label: 'Altro' }
];

export default function AdminFinancialDashboard() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    descrizione: '',
    categoria: 'varie',
    importo: '',
    data: new Date().toISOString().split('T')[0],
    ricorrente: false,
    fornitore: '',
    metodo_pagamento: '',
    note: ''
  });

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchExpenses();
    }
  }, [token, selectedYear]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/financial/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching financial dashboard:', error);
      toast.error('Errore nel caricamento del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenses = async () => {
    try {
      const currentMonth = `${selectedYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
      const response = await axios.get(`${API}/admin/financial/expenses?mese=${currentMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const handleSaveExpense = async () => {
    if (!expenseForm.descrizione || !expenseForm.importo) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    try {
      const payload = {
        ...expenseForm,
        importo: parseFloat(expenseForm.importo)
      };

      if (editingExpense) {
        await axios.put(`${API}/admin/financial/expenses/${editingExpense.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Spesa aggiornata!');
      } else {
        await axios.post(`${API}/admin/financial/expenses`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Spesa aggiunta!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
      fetchDashboard();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Eliminare questa spesa?')) return;
    
    try {
      await axios.delete(`${API}/admin/financial/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Spesa eliminata');
      fetchExpenses();
      fetchDashboard();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const resetForm = () => {
    setExpenseForm({
      descrizione: '',
      categoria: 'varie',
      importo: '',
      data: new Date().toISOString().split('T')[0],
      ricorrente: false,
      fornitore: '',
      metodo_pagamento: '',
      note: ''
    });
    setEditingExpense(null);
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      descrizione: expense.descrizione,
      categoria: expense.categoria,
      importo: expense.importo.toString(),
      data: expense.data,
      ricorrente: expense.ricorrente || false,
      fornitore: expense.fornitore || '',
      metodo_pagamento: expense.metodo_pagamento || '',
      note: expense.note || ''
    });
    setDialogOpen(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getCategoryInfo = (cat) => {
    return EXPENSE_CATEGORIES.find(c => c.value === cat) || EXPENSE_CATEGORIES[7];
  };

  if (loading) {
    return (
      <AdminLayout title="Gestionale Finanziario">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  const ytd = dashboard?.ytd || {};
  const meseCorrente = dashboard?.mese_corrente || {};
  const annuale = dashboard?.annuale || {};
  const trend = dashboard?.trend_vs_anno_precedente;

  return (
    <AdminLayout title="Gestionale Finanziario">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-sm text-gray-500">
              Panoramica finanziaria {selectedYear}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/financial/timeline">
              <Button variant="outline" data-testid="financial-timeline-btn">
                <Receipt className="w-4 h-4 mr-2" />
                Timeline
              </Button>
            </Link>
            <Link to="/admin/financial/settings">
              <Button variant="outline" data-testid="financial-settings-btn">
                <Settings className="w-4 h-4 mr-2" />
                Impostazioni %
              </Button>
            </Link>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#C5A059] hover:bg-[#B08D4A] text-white" data-testid="add-expense-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Spesa
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingExpense ? 'Modifica Spesa' : 'Nuova Spesa'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Data spesa - FASE 1: Campo prominente */}
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <Label htmlFor="data" className="text-amber-800 font-medium flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Data Spesa *
                    </Label>
                    <Input
                      id="data"
                      type="date"
                      value={expenseForm.data}
                      onChange={(e) => setExpenseForm({...expenseForm, data: e.target.value})}
                      className="mt-1 border-amber-300 focus:border-amber-500"
                      data-testid="expense-data"
                    />
                    <p className="text-xs text-amber-600 mt-1">
                      La spesa sarà attribuita al mese di questa data
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="descrizione">Descrizione *</Label>
                    <Input
                      id="descrizione"
                      value={expenseForm.descrizione}
                      onChange={(e) => setExpenseForm({...expenseForm, descrizione: e.target.value})}
                      placeholder="Es: Lavanderia biancheria ospiti"
                      data-testid="expense-descrizione"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="categoria">Categoria *</Label>
                      <select
                        id="categoria"
                        value={expenseForm.categoria}
                        onChange={(e) => setExpenseForm({...expenseForm, categoria: e.target.value})}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid="expense-categoria"
                      >
                        {EXPENSE_CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="importo">Importo (€) *</Label>
                      <Input
                        id="importo"
                        type="number"
                        step="0.01"
                        value={expenseForm.importo}
                        onChange={(e) => setExpenseForm({...expenseForm, importo: e.target.value})}
                        placeholder="100.00"
                        data-testid="expense-importo"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fornitore">Fornitore</Label>
                      <Input
                        id="fornitore"
                        value={expenseForm.fornitore}
                        onChange={(e) => setExpenseForm({...expenseForm, fornitore: e.target.value})}
                        placeholder="Es: Lavanderia Rossi"
                        data-testid="expense-fornitore"
                      />
                    </div>
                    <div>
                      <Label htmlFor="metodo_pagamento">Metodo Pagamento</Label>
                      <select
                        id="metodo_pagamento"
                        value={expenseForm.metodo_pagamento}
                        onChange={(e) => setExpenseForm({...expenseForm, metodo_pagamento: e.target.value})}
                        className="w-full border rounded-md px-3 py-2 text-sm"
                        data-testid="expense-metodo"
                      >
                        <option value="">-- Seleziona --</option>
                        {PAYMENT_METHODS.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="note">Note</Label>
                    <textarea
                      id="note"
                      value={expenseForm.note}
                      onChange={(e) => setExpenseForm({...expenseForm, note: e.target.value})}
                      placeholder="Eventuali note aggiuntive..."
                      className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]"
                      data-testid="expense-note"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ricorrente"
                      checked={expenseForm.ricorrente}
                      onChange={(e) => setExpenseForm({...expenseForm, ricorrente: e.target.checked})}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor="ricorrente" className="text-sm cursor-pointer">
                      Spesa ricorrente mensile
                    </Label>
                  </div>

                  <Button 
                    onClick={handleSaveExpense} 
                    className="w-full bg-[#C5A059] hover:bg-[#B08D4A]"
                    data-testid="save-expense-btn"
                  >
                    {editingExpense ? 'Aggiorna' : 'Salva Spesa'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Entrate YTD</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(ytd.totale_lordo)}</p>
                </div>
                <div className="p-2 bg-green-100 rounded-full">
                  <Euro className="w-5 h-5 text-green-600" />
                </div>
              </div>
              {trend !== null && (
                <div className={`flex items-center mt-2 text-xs ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {trend >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
                  {trend >= 0 ? '+' : ''}{trend}% vs anno precedente
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Netto YTD</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(ytd.totale_netto)}</p>
                </div>
                <div className="p-2 bg-blue-100 rounded-full">
                  <PiggyBank className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Dopo commissioni e cedolare
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Spese YTD</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(ytd.totale_spese)}</p>
                </div>
                <div className="p-2 bg-red-100 rounded-full">
                  <Receipt className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Fitto, utenze, manutenzione...
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-[#C5A059]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Profitto YTD</p>
                  <p className={`text-2xl font-bold ${ytd.profitto_netto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(ytd.profitto_netto)}
                  </p>
                </div>
                <div className="p-2 bg-[#C5A059]/10 rounded-full">
                  {ytd.profitto_netto >= 0 ? (
                    <TrendingUp className="w-5 h-5 text-[#C5A059]" />
                  ) : (
                    <TrendingDown className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Netto - Spese operative
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Mese Corrente e Spese */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Mese Corrente */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C5A059]" />
                Mese Corrente: {meseCorrente.mese || 'N/A'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">Lordo</span>
                  <span className="font-semibold">{formatCurrency(meseCorrente.totale_lordo)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">- Commissioni OTA</span>
                  <span className="font-semibold text-red-600">-{formatCurrency(meseCorrente.totale_commissioni)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">- Cedolare Secca</span>
                  <span className="font-semibold text-orange-600">-{formatCurrency(meseCorrente.cedolare_secca)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">= Netto</span>
                  <span className="font-bold text-blue-600">{formatCurrency(meseCorrente.totale_netto)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="text-sm text-gray-600">- Spese Operative</span>
                  <span className="font-semibold text-red-600">-{formatCurrency(meseCorrente.totale_spese)}</span>
                </div>
                <div className="flex justify-between items-center py-2 bg-[#C5A059]/10 px-2 rounded">
                  <span className="font-medium">= PROFITTO</span>
                  <span className={`font-bold text-lg ${meseCorrente.profitto_netto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(meseCorrente.profitto_netto)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 text-center text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500">Prenotazioni</p>
                    <p className="font-bold">{meseCorrente.num_prenotazioni || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500">Prezzo medio/notte</p>
                    <p className="font-bold">{formatCurrency(meseCorrente.prezzo_medio_notte)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Spese Recenti */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-[#C5A059]" />
                  Spese Mese Corrente
                </span>
                <span className="text-sm font-normal text-gray-500">
                  Totale: {formatCurrency(expenses.reduce((sum, e) => sum + e.importo, 0))}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {expenses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Receipt className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>Nessuna spesa registrata questo mese</p>
                  <Button 
                    variant="link" 
                    onClick={() => setDialogOpen(true)}
                    className="mt-2 text-[#C5A059]"
                  >
                    Aggiungi la prima spesa
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {expenses.map((expense) => {
                    const catInfo = getCategoryInfo(expense.categoria);
                    const IconComponent = catInfo.icon;
                    return (
                      <div 
                        key={expense.id} 
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg group"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${catInfo.color}`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{expense.descrizione}</p>
                            <p className="text-xs text-gray-500">{expense.data}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-red-600">
                            -{formatCurrency(expense.importo)}
                          </span>
                          <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => openEditDialog(expense)}
                              className="h-7 w-7 p-0"
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDeleteExpense(expense.id)}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Spese per Categoria */}
        {dashboard?.spese_per_categoria && Object.keys(dashboard.spese_per_categoria).length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Ripartizione Spese per Categoria (YTD)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(dashboard.spese_per_categoria).map(([cat, amount]) => {
                  const catInfo = getCategoryInfo(cat);
                  const IconComponent = catInfo.icon;
                  return (
                    <div key={cat} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-1.5 rounded ${catInfo.color}`}>
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium capitalize">{catInfo.label}</span>
                      </div>
                      <p className="text-lg font-bold">{formatCurrency(amount)}</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
